import DriveSidebar from 'components/layout/DriveSidebar'
import SectionMessage from 'components/layout/SectionMessage'
import Panel, { PanelHeading } from 'components/layout/Panel'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import { states } from 'types/states'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Container, Form, Icon, Spacer, Text } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import { initialModalOptions } from 'types/global'
import { SubmittedModal } from '../SubmittedModal'
import { postSalesforceCase } from 'actions/salesforce'
import Helmet from 'react-helmet'
import { useLocalization, LocalizedText } from 'localization'

const AppealFormPage: React.FC = () => {
  const { t } = useLocalization()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const profile = useSelector<any, any>((state) => state.profile)
  const [checked, setChecked] = useState(false)
  const [acknowledgementError, setAcknowledgementError] = useState(false)
  const [modalOptions, setModalOptions] = useState(initialModalOptions)

  const dispatch = useDispatch()

  const handleCheckAck = useCallback((checked: boolean) => {
    setChecked(checked)
    if (checked) {
      setAcknowledgementError(false)
    }
  }, [])

  const handleSubmit = useCallback(
    async (formData: any) => {
      if (!checked) {
        setAcknowledgementError(true)
        return
      }
      setAcknowledgementError(false)
      try {
        const payload = {
          case_type: 'appeal',
          support_id: formData.support_id,
          rich_description: formData.description,
          state: formData.gig_state,
          profile_id: profile.id,
        }
        await postSalesforceCase(payload).then(() => {
          setModalOptions({ modalOpen: true, modalType: 'appealSubmitted' })
        })
      } catch (error) {
        dispatch({
          type: 'PUSH_FLASH',
          flash: { messageKey: 'dweb_flash_appeal_submit_failed_try_again_later', type: 'error' },
        })
        throw new Error()
      }
    },
    [checked, profile.id, dispatch]
  )

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_dashboard_salesforce_appeal_appeal_form_submit_appeal')}</title>
      </Helmet>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_my_profile'), linkTo: '/dashboard' },
            { label: t('dweb_dashboard_account_status'), linkTo: 'account-status' },
            { label: t('dweb_dashboard_salesforce_appeal_appeal_form_submit_appeal') },
          ]}
        />
      )}
      <Panel>
        <Container.Base $padding="2.4rem 0">
          {!isWebView && (
            <PanelHeading>
              <LocalizedText translationKey="dweb_dashboard_salesforce_appeal_appeal_form_submit_appeal" />
            </PanelHeading>
          )}
          <Spacer $height="2.4rem" />
          <SectionMessage description={t('dweb_appeal_form_submission_deadline')} iconType="warning" />
          <Spacer $height="2.4rem" />
          <Form.Basic
            id="appeal-form"
            onSubmit={handleSubmit}
            validations={[
              ['gig_state', Form.validations.required],
              ['description', Form.validations.required],
            ]}
          >
            <Form.Select label={t('dweb_dashboard_salesforce_appeal_appeal_form_state_gig_location')} name="gig_state">
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </Form.Select>
            <Spacer $height="1.6rem" />
            <Form.Input
              label={t('dweb_dashboard_salesforce_appeal_appeal_form_support_id_optional')}
              name="support_id"
            />
            <Spacer $height="1.6rem" />
            <Form.Input
              label={t('dweb_dashboard_salesforce_appeal_appeal_form_description_appeal')}
              name="description"
              maxLength={1500}
            />
            <Spacer $height="2.4rem" />
            <Container.Grid $gridTemplateColumns="2.8rem 1fr" $alignItems="center">
              <Form.Checkbox id="confirmation" name="confirmation" checked={checked} onChange={handleCheckAck} />
              <label htmlFor="marketing_comm_opt_in">
                <Text.Caption $textAlign="left" $color="neutral-black">
                  <LocalizedText translationKey="dweb_appeal_form_perjury_acknowledgement" />
                </Text.Caption>
              </label>
            </Container.Grid>
            <Spacer $height="2.4rem" />
            {acknowledgementError && (
              <Container.Grid $gridTemplateColumns="2.8rem 1fr">
                <Icon.AlertFilled width="2rem" $color="error-base" />
                <Container.GridItem>
                  <Text.Caption $textAlign="left" $color="neutral-black">
                    <LocalizedText translationKey="dweb_appeal_form_acknowledgement_required" />
                  </Text.Caption>
                  <Spacer $height="1.6rem" />
                </Container.GridItem>
              </Container.Grid>
            )}
            <Form.Submit>
              <LocalizedText translationKey="dweb_common_submit" />
            </Form.Submit>
            <Spacer $height="2.4rem" />
            <Button.Link.Tertiary to={'account-status'} $width={'100%'}>
              <LocalizedText translationKey="dweb_common_cancel" />
            </Button.Link.Tertiary>
          </Form.Basic>
        </Container.Base>
      </Panel>

      <SubmittedModal
        modalOptions={modalOptions}
        isWebView={isWebView}
        linkTo={'appeal-status'}
        modalDescription={t('dweb_dashboard_salesforce_appeal_appeal_form_appeal_submitted_confirmation')}
      />
    </Page>
  )
}

export default AppealFormPage
